import React, { useState } from 'react';
import { MAX_CONTENT_WIDTH } from '../constants/layout';

export default function GetStartedPage() {
  const [appIcon, setAppIcon] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [appName, setAppName] = useState('');
  const [description, setDescription] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAppIcon(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAppIcon(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      paddingTop: '0',
      paddingBottom: '60px'
    }}>
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          <h1 style={{
            color: 'var(--color-text-primary)',
            fontSize: '36px',
            fontWeight: '700',
            marginBottom: '16px',
            letterSpacing: '-0.05em',
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}>
            Get Started
          </h1>
          <p style={{
            color: 'var(--color-text-secondary)',
            fontSize: '16px',
            lineHeight: '1.6',
            maxWidth: '600px',
            margin: '0 auto',
            letterSpacing: '-0.02em',
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}>
            Let's start building your website and mobile apps.
          </p>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px'
        }}>
          {/* App Icon and Name Row */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '40px',
            width: '100%',
            maxWidth: '700px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {/* App Icon Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              flexShrink: 0
            }}>
              {/* App Icon Preview */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  width: '200px',
                  height: '200px',
                  clipPath: 'url(#squircle-clip)',
                  overflow: 'hidden',
                  backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.05)' : 'var(--color-surface-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                {appIcon ? (
                  <img
                    src={appIcon}
                    alt="App icon preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                ) : (
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--color-text-secondary)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ opacity: 0.4 }}
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                )}
              </div>

              <input
                id="file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {/* Upload Button */}
              <button
                onClick={() => document.getElementById('file-input').click()}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--color-surface-muted)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--color-surface)';
                }}
              >
                {appIcon ? 'Change Icon' : 'Upload Icon'}
              </button>
            </div>

            {/* App Name Input */}
            <div style={{
              flex: '1 1 300px',
              minWidth: '300px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <label
                htmlFor="app-name"
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
              >
                App/Website Name
              </label>
              <input
                id="app-name"
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="Enter your app or website name"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-surface)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--color-accent)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--color-border)';
                }}
              />

              {/* Description Input */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '24px'
              }}>
                <label
                  htmlFor="app-description"
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif'
                  }}
                >
                  Brief Description
                </label>
                <textarea
                  id="app-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us what your business does"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-accent)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                  }}
                />
              </div>

              {/* Primary Color Picker */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '24px'
              }}>
                <label
                  htmlFor="primary-color"
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif'
                  }}
                >
                  Primary Color
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <input
                    id="primary-color"
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={{
                      width: '80px',
                      height: '50px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent'
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text-primary)',
                      fontFamily: 'Helvetica, Arial, sans-serif'
                    }}>
                      {primaryColor.toUpperCase()}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      fontFamily: 'Helvetica, Arial, sans-serif'
                    }}>
                      Used for buttons, links, and accents
                    </span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div style={{
                width: '100%',
                height: '1px',
                backgroundColor: 'var(--color-border)',
                marginTop: '32px',
                marginBottom: '32px'
              }} />

              {/* User Name Input */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <label
                  htmlFor="user-name"
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif'
                  }}
                >
                  Your Name
                </label>
                <input
                  id="user-name"
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your full name"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-accent)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                  }}
                />
              </div>

              {/* User Email Input */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '24px'
              }}>
                <label
                  htmlFor="user-email"
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif'
                  }}
                >
                  Email Address
                </label>
                <input
                  id="user-email"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-accent)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            marginTop: '48px'
          }}>
            <button
              disabled={!(appIcon && appName.trim() && userName.trim() && userEmail.trim())}
              style={{
                backgroundColor: (appIcon && appName.trim() && userName.trim() && userEmail.trim()) ? primaryColor : 'var(--color-surface-muted)',
                color: (appIcon && appName.trim() && userName.trim() && userEmail.trim()) ? 'white' : 'var(--color-text-secondary)',
                border: 'none',
                borderRadius: '8px',
                padding: '16px 64px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (appIcon && appName.trim() && userName.trim() && userEmail.trim()) ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                fontFamily: 'Helvetica, Arial, sans-serif',
                opacity: (appIcon && appName.trim() && userName.trim() && userEmail.trim()) ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (appIcon && appName.trim() && userName.trim() && userEmail.trim()) {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                if (appIcon && appName.trim() && userName.trim() && userEmail.trim()) {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.opacity = '1';
                } else {
                  e.target.style.opacity = '0.5';
                }
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
